import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, fileUrl, formatApiError, uploadImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UsersManager } from "@/components/UsersManager";
import { WhatsAppAutoSettings } from "@/components/WhatsAppAutoSettings";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...s, client_percentage: Number(s.client_percentage) });
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <p className="text-[var(--muted)]">Caricamento...</p>;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <div className="space-y-8 max-w-3xl">
      <div className="fade-up">
        <p className="eyebrow">Configurazione</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Impostazioni</h1>
        {!isAdmin && <p className="text-sm text-[var(--muted)] mt-2">Solo l'amministratore può modificare queste impostazioni.</p>}
      </div>
      <section className="rounded-2xl bg-white border border-[var(--border)] p-5 sm:p-6 space-y-5" data-testid="settings-form">
        <h2 className="font-heading text-lg font-semibold">Contratto e testi legali</h2>
        <div className="grid sm:grid-cols-[1fr_140px_160px] gap-4">
          <div className="space-y-1.5"><Label>Nome attività</Label><Input disabled={!isAdmin} value={s.shop_name} onChange={set("shop_name")} data-testid="settings-shop-name-input" /></div>
          <div className="space-y-1.5"><Label>Quota cliente %</Label><Input type="number" min="0" max="100" disabled={!isAdmin} value={s.client_percentage} onChange={set("client_percentage")} data-testid="settings-percentage-input" /></div>
          <div className="space-y-1.5"><Label>Ora riepilogo WhatsApp</Label><Input type="time" disabled={!isAdmin} value={s.whatsapp_time} onChange={set("whatsapp_time")} data-testid="settings-whatsapp-time-input" /></div>
        </div>
        {isAdmin && <WhatsAppAutoSettings settings={s} setSettings={setS} />}
        <div className="space-y-1.5"><Label>Condizioni del contratto (lette e accettate dal cliente)</Label><Textarea rows={10} disabled={!isAdmin} value={s.conditions_text} onChange={set("conditions_text")} data-testid="settings-conditions-input" /></div>
        <div className="space-y-1.5"><Label>Informativa privacy / consenso trattamento dati</Label><Textarea rows={8} disabled={!isAdmin} value={s.privacy_text} onChange={set("privacy_text")} data-testid="settings-privacy-input" /></div>
        <div className="space-y-1.5"><Label>Condizioni commerciali delle Impegnative (riportate nel documento)</Label><Textarea rows={8} disabled={!isAdmin} value={s.impegnative_conditions || ""} onChange={set("impegnative_conditions")} data-testid="settings-imp-conditions-input" /></div>
        <div className="space-y-2" data-testid="settings-logo">
          <Label>Logo SUK (per i PDF delle Impegnative)</Label>
          <div className="flex items-center gap-3">
            {s.logo_path ? <img src={fileUrl(s.logo_path)} alt="Logo" className="h-14 max-w-[160px] object-contain rounded border bg-white p-1" data-testid="settings-logo-preview" /> : <span className="text-sm text-[var(--muted)]">Nessun logo: nel PDF compare la scritta SUK</span>}
            {isAdmin && (
              <label className="btn-secondary h-10 px-4 cursor-pointer" data-testid="settings-logo-upload">
                Carica logo
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={async (e) => { const file = e.target.files?.[0]; e.target.value = ""; if (!file) return; try { const p = await uploadImage(file); setS((x) => ({ ...x, logo_path: p })); toast.success("Logo caricato: ricorda di salvare"); } catch (err) { toast.error(formatApiError(err)); } }} data-testid="settings-logo-input" />
              </label>
            )}
          </div>
        </div>
        {isAdmin && <button onClick={save} disabled={saving} className="btn-primary h-11 px-6" data-testid="settings-save-button">{saving ? "Salvataggio..." : "Salva impostazioni"}</button>}
      </section>
      {isAdmin && <UsersManager currentId={user.id} />}
    </div>
  );
}
